import apiGetPushNoticeListLittleBell, { TResponse as TGetPushNoticeListLittleBellResponse } from '@/api/NotificationManagement/notificationCenter/GetPushNoticeListLittleBell';
import apiPushNoticeReadLittleBell from '@/api/NotificationManagement/notificationCenter/postPushNoticeReadLittleBell';
import { computed, Injectable, signal } from '@angular/core';
import { DateTime } from 'luxon';

export type TNoticeItem = TGetPushNoticeListLittleBellResponse['data'][number] & { displayDate?: string };

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
    private _personal = signal<TNoticeItem[]>([]);
    private _system = signal<TNoticeItem[]>([]);

    // internal timer id for auto refresh
    private _autoRefreshHandle: ReturnType<typeof setInterval> | undefined;

    // Expose read functions for template usage
    personal = this._personal;
    system = this._system;

    unreadPersonal = computed(() => this._personal().filter((n) => !n.isRead).length);
    unreadSystem = computed(() => this._system().filter((n) => !n.isRead).length);
    unreadTotal = computed(() => this.unreadPersonal() + this.unreadSystem());

    async refresh(): Promise<void> {
        if (!localStorage.getItem('accessToken')) {
            return;
        }

        const response = await apiGetPushNoticeListLittleBell();
        const personal: TNoticeItem[] = [];
        const system: TNoticeItem[] = [];

        response.data.data.forEach((item) => {
            const enriched: TNoticeItem = {
                ...item,
                displayDate: DateTime.fromISO(item.date).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
            };
            if (item.type === 'Personal') {
                personal.push(enriched);
            }
            else {
                system.push(enriched);
            }
        });

        this._personal.set(personal);
        this._system.set(system);
    }

    startAutoRefresh(intervalMs = 60000) {
        this.stopAutoRefresh();
        void this.refresh();
        this._autoRefreshHandle = setInterval(() => {
            void this.refresh();
        }, intervalMs);
    }

    stopAutoRefresh() {
        if (this._autoRefreshHandle) {
            clearInterval(this._autoRefreshHandle);
            this._autoRefreshHandle = undefined;
        }
    }

    async markAsRead(id: number): Promise<void> {
        await apiPushNoticeReadLittleBell({ ids: [id] });
        this._personal.update((list) => list.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
        this._system.update((list) => list.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    }
}
